import * as THREE from "three";
import { EffectComposer } from "three/addons/postprocessing/EffectComposer.js";
import { RenderPass } from "three/addons/postprocessing/RenderPass.js";
import { UnrealBloomPass } from "three/addons/postprocessing/UnrealBloomPass.js";
import { SMAAPass } from "three/addons/postprocessing/SMAAPass.js";
import { FXAAPass } from "three/addons/postprocessing/FXAAPass.js";
import { OutputPass } from "three/addons/postprocessing/OutputPass.js";
import { ShaderPass } from "three/addons/postprocessing/ShaderPass.js";
import { VignetteShader } from "three/addons/shaders/VignetteShader.js";
import { ColorCorrectionShader } from "three/addons/shaders/ColorCorrectionShader.js";

import type { ResolvedQuality } from "./quality";

/**
 * Gated post stack: mild bloom (emissives), SMAA/FXAA, industrial vignette + grade.
 * Heavy passes follow quality / reducedMotion — Low skips the composer entirely.
 */
export class PostFxPipeline {
  private composer: EffectComposer | null = null;
  private renderPass: RenderPass | null = null;
  private bloomPass: UnrealBloomPass | null = null;
  private smaaPass: SMAAPass | null = null;
  private fxaaPass: FXAAPass | null = null;
  private vignettePass: ShaderPass | null = null;
  private gradePass: ShaderPass | null = null;
  private outputPass: OutputPass | null = null;
  private enabled = false;
  private width = 1;
  private height = 1;

  public constructor(
    private readonly renderer: THREE.WebGLRenderer,
    private readonly scene: THREE.Scene,
    private readonly camera: THREE.Camera,
  ) {}

  public applyQuality(quality: ResolvedQuality): void {
    if (!quality.postFx) {
      this.disposeComposer();
      this.enabled = false;
      return;
    }

    this.ensureComposer();
    this.enabled = true;

    if (this.bloomPass) {
      this.bloomPass.enabled = quality.bloom;
      this.bloomPass.strength = quality.bloomStrength;
      this.bloomPass.radius = quality.bloomRadius;
      this.bloomPass.threshold = quality.bloomThreshold;
    }
    if (this.smaaPass) this.smaaPass.enabled = quality.antialiasPass === "smaa";
    if (this.fxaaPass) this.fxaaPass.enabled = quality.antialiasPass === "fxaa";
    if (this.vignettePass) {
      this.vignettePass.enabled = quality.vignette;
      this.vignettePass.uniforms["offset"]!.value = 0.95;
      this.vignettePass.uniforms["darkness"]!.value = 0.55;
    }
    if (this.gradePass) {
      this.gradePass.enabled = quality.colorGrade;
      this.gradePass.uniforms["powRGB"]!.value.set(1.05, 1.02, 0.98);
      this.gradePass.uniforms["mulRGB"]!.value.set(1.02, 0.99, 0.94);
      this.gradePass.uniforms["addRGB"]!.value.set(0.01, 0.005, 0);
    }
  }

  public setSize(width: number, height: number): void {
    this.width = Math.max(1, width);
    this.height = Math.max(1, height);
    if (!this.composer) return;
    this.composer.setSize(this.width, this.height);
    this.bloomPass?.setSize(this.width, this.height);
    this.smaaPass?.setSize(this.width, this.height);
    this.fxaaPass?.setSize(this.width, this.height);
  }

  public setPixelRatio(ratio: number): void {
    this.composer?.setPixelRatio(ratio);
  }

  public render(): void {
    if (this.enabled && this.composer) {
      this.composer.render();
      return;
    }
    this.renderer.render(this.scene, this.camera);
  }

  public dispose(): void {
    this.disposeComposer();
  }

  private ensureComposer(): void {
    if (this.composer) return;

    this.composer = new EffectComposer(this.renderer);
    this.renderPass = new RenderPass(this.scene, this.camera);
    this.composer.addPass(this.renderPass);

    const res = new THREE.Vector2(this.width, this.height);
    this.bloomPass = new UnrealBloomPass(res, 0.28, 0.32, 0.84);
    this.composer.addPass(this.bloomPass);

    this.gradePass = new ShaderPass(ColorCorrectionShader);
    this.composer.addPass(this.gradePass);

    this.vignettePass = new ShaderPass(VignetteShader);
    this.composer.addPass(this.vignettePass);

    this.smaaPass = new SMAAPass();
    this.composer.addPass(this.smaaPass);

    this.outputPass = new OutputPass();
    this.composer.addPass(this.outputPass);

    this.fxaaPass = new FXAAPass();
    this.fxaaPass.enabled = false;
    this.composer.addPass(this.fxaaPass);

    this.composer.setSize(this.width, this.height);
  }

  private disposeComposer(): void {
    this.composer?.dispose();
    this.composer = null;
    this.renderPass = null;
    this.bloomPass = null;
    this.smaaPass = null;
    this.fxaaPass = null;
    this.vignettePass = null;
    this.gradePass = null;
    this.outputPass = null;
  }
}
